import React, { useEffect, useState } from 'react';
import { Button } from '@/components';
import {
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';
import useFinancialPartnerStore from '@core/financialPartner/useFinancialPartnerStore';
import { SetupIntent } from '@core/generated/gql-types';
import { useTenantId } from '@/contexts/TenantContext';

const Bills = () => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [setupIntent, setSetupIntent] = useState<SetupIntent | null>(null);
  const tenantId = useTenantId();
  const {
    customer,
    paymentMethods,
    isLoadingCustomer,
    isLoadingPaymentMethods,
    customerError,
    paymentMethodsError,
    fetchCustomerAndPaymentMethods,
    deletePaymentMethod,
    createSetupIntent,
  } = useFinancialPartnerStore();

  const handleAddPaymentMethod = async () => {
    if (!stripe || !elements || isProcessing) {
      return;
    }

    setIsProcessing(true);
    setMessage('');

    try {
      // First, submit the elements to validate the form
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setMessage(submitError.message || 'Form validation failed');
        toast.error(submitError.message || 'Please check your payment details');
        return;
      }

      // Create setup intent if not already created
      let setupIntentResponse = setupIntent;
      if (!setupIntentResponse && customer?.id) {
        setupIntentResponse = await createSetupIntent(customer?.id);
      }

      if (!setupIntentResponse?.clientSecret) {
        toast.error('Failed to create setup intent');
        return;
      }

      // Confirm the setup intent
      const result = await stripe.confirmSetup({
        elements,
        clientSecret: setupIntentResponse.clientSecret,
        confirmParams: {
          return_url: window.location.href, // Return to current page
        },
        redirect: 'if_required',
      });

      if (result.error) {
        setMessage(
          result.error.message ||
            'An error occurred while adding payment method.',
        );
        toast.error(result.error.message || 'Failed to add payment method');
      } else if (result.setupIntent) {
        // Payment method added successfully
        toast.success('Payment method added successfully!');
        setShowPaymentForm(false);
        setSetupIntent(null);

        // Refresh payment methods
        if (tenantId) {
          await fetchCustomerAndPaymentMethods(tenantId);
        }
      }
    } catch (error) {
      setMessage('An unexpected error occurred.');
      toast.error('An unexpected error occurred');
      console.error('Error adding payment method:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeletePaymentMethod = async (paymentMethodId: string) => {
    if (
      !window.confirm('Are you sure you want to delete this payment method?')
    ) {
      return;
    }

    const response = await deletePaymentMethod(paymentMethodId, tenantId || '');
    if (response && response.success) {
      toast.success('Payment method deleted successfully');
    } else {
      toast.error('Failed to delete payment method');
    }
  };

  const handleCancel = () => {
    if (isProcessing) return;
    setShowPaymentForm(false);
    setShowDisclaimer(false);
    setMessage('');
    setSetupIntent(null);
  };

  const handleDisclaimerAccept = () => {
    setDisclaimerAccepted(true);
    setShowDisclaimer(false);
    setShowPaymentForm(true);
  };

  const handleDisclaimerDecline = () => {
    setDisclaimerAccepted(false);
    setShowDisclaimer(false);
  };

  // // Create setup intent when payment form is shown
  // useEffect(() => {
  //   if (showPaymentForm && !setupIntent && customer?.id) {
  //     createSetupIntent(customer?.id).catch(() => {
  //       setShowPaymentForm(false);
  //     });
  //   }
  // }, [showPaymentForm, customer?.id]);

  useEffect(() => {
    if (tenantId) {
      fetchCustomerAndPaymentMethods(tenantId);
    }
  }, [tenantId, fetchCustomerAndPaymentMethods]);

  return (
    <div>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold text-gray-900">Bills</h1>
          <p className="text-gray-600">
            Manage your payment methods and view bills
          </p>
        </div>

        {/* Loading and error states */}
        {(isLoadingCustomer || isLoadingPaymentMethods) && (
          <div className="text-blue-500">Loading...</div>
        )}
        {customerError && <div className="text-red-500">{customerError}</div>}
        {paymentMethodsError && (
          <div className="text-red-500">{paymentMethodsError}</div>
        )}

        {/* No Customer - Contact Z Rec HQ */}
        {!isLoadingCustomer && !customer && !customerError && (
          <div className="rounded-lg border bg-white p-6">
            <div className="text-center">
              <svg
                className="mx-auto mb-4 h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20v-2a3 3 0 01-5.356-1.857M17 10 0v-2c0-0.656-0.126-10.283-.356-1.857 202v-2 3 0015.356-18577 202c0-.656.126-1.283.356-1.857m0a502 5002 0 0190.288 0M15 7a330 11-6 0 3 3 0016 0zm6 3a220 11-40 2 2 0 14 0zM7 10a220 114z"
                />
              </svg>
              <h3 className="mb-2 text-lg font-medium text-gray-900">
                No Customer Account Found
              </h3>
              <p className="mb-4">
                Your customer account hasnt been set up yet. Please contact Z
                Rec HQ to create your customer account.
              </p>
              <div className="space-y-2">
                <p className="text-sm text-gray-500">
                  <strong>Contact Information:</strong>
                </p>
                <p className="text-sm text-gray-500">
                  Email: support@zrecruitment.com.au
                </p>
                <p className="text-sm text-gray-500">Phone: (555) 123-4567 </p>
              </div>
            </div>
          </div>
        )}

        {/* Customer exists - Show payment methods or add button */}
        {!isLoadingCustomer && customer && (
          <div className="space-y-4">
            {' '}
            {/* Customer Info */}
            <div className="rounded-lg border bg-white p-4">
              <h3 className="mb-2 text-lg font-medium text-gray-900">
                Customer Information
              </h3>
              <div className="text-sm text-gray-600">
                <p>
                  <strong>Email:</strong> {customer.email}
                </p>
                <p>
                  <strong>Customer ID:</strong> {customer.id}
                </p>
              </div>
            </div>
            {/* Payment Methods Section */}
            <div className="rounded-lg border bg-white p-6">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Payment Method
                </h3>
                {!isLoadingPaymentMethods &&
                  (!paymentMethods || paymentMethods.length === 0) && (
                    <Button
                      variant="primary"
                      onClick={() => setShowDisclaimer(true)}
                      className="w-fit"
                    >
                      Add Payment Method
                    </Button>
                  )}
              </div>

              {/* Loading payment methods */}
              {isLoadingPaymentMethods && (
                <div className="py-8 text-center text-gray-500">
                  <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
                  <p>Loading payment methods...</p>
                </div>
              )}

              {/* Payment methods list */}
              {!isLoadingPaymentMethods &&
                paymentMethods &&
                paymentMethods.length > 0 && (
                  <div className="space-y-3">
                    {paymentMethods.map((method) => (
                      <div
                        key={method.id}
                        className="flex items-center justify-between rounded-lg border p-4"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="flex h-10 w-16 items-center justify-center rounded border bg-gray-50">
                            {method.card?.brand && (
                              <span className="text-xs font-medium text-gray-600 uppercase">
                                {method.card.brand}
                              </span>
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              •••• •••• •••• {method.card?.last4}
                            </p>
                            <p className="text-sm text-gray-500">
                              Expires {method.card?.expMonth}/
                              {method.card?.expYear}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {method.card?.brand && (
                            <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                              {method.card.brand}
                            </span>
                          )}
                          <Button
                            variant="outline"
                            onClick={() => handleDeletePaymentMethod(method.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

              {/* No payment methods */}
              {!isLoadingPaymentMethods &&
                (!paymentMethods || paymentMethods.length === 0) && (
                  <div className="py-8 text-center text-gray-500">
                    <svg
                      className="mx-auto mb-4 h-12 w-12 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                      />
                    </svg>
                    <p className="mb-2">No payment method added yet</p>
                    <p className="text-sm">
                      Add a payment method to get started
                    </p>
                  </div>
                )}
            </div>
          </div>
        )}

        {/* Disclaimer Modal */}
        {showDisclaimer && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-transparent">
            <div className="w-full max-w-2xl rounded-lg border bg-white p-6 shadow-xl">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Payment Disclaimer
                </h3>
                <button
                  onClick={handleDisclaimerDecline}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="mb-6 max-h-96 overflow-y-auto">
                <div className="space-y-4 text-sm text-gray-700">
                  <p>
                    Our website uses Stripe, a third-party payment processor, to
                    handle all financial transactions. By saving a payment
                    method, you agree to the following:
                  </p>

                  <ol className="ml-4 list-inside list-decimal space-y-2">
                    <li>
                      Your payment information is not stored on our servers. All
                      payment data is securely processed and stored by Stripe.
                    </li>
                    <li>
                      We do not have access to your full credit card details.
                      For your security, this information is handled directly by
                      Stripe.
                    </li>
                    <li>
                      By making a payment, you agree to Stripe's{' '}
                      <a
                        href="https://stripe.com/legal"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a
                        href="https://stripe.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        Privacy Policy
                      </a>
                      .
                    </li>
                    <li>
                      While we strive to ensure the security of all
                      transactions, we cannot guarantee against unauthorized
                      access or data breaches on Stripe's systems.
                    </li>
                    <li>
                      For any issues related to payment processing, please
                      contact us first. If necessary, we may direct you to
                      Stripe's customer support.
                    </li>
                    <li>
                      We reserve the right to refuse service to anyone for any
                      reason at any time.
                    </li>
                  </ol>

                  <p>
                    Please review our full Terms of Service and Privacy Policy
                    for more information about how we handle your data and
                    process transactions.
                  </p>

                  <p className="font-medium">
                    By using our services, you acknowledge that you have read
                    and agree to this disclaimer.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={handleDisclaimerAccept} className="flex-1">
                  I Agree & Continue
                </Button>
                <button
                  onClick={handleDisclaimerDecline}
                  className="flex-1 rounded-md border border-gray-300 px-4 py-3 font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Form Modal */}
        {showPaymentForm && customer && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-transparent">
            <div className="w-full max-w-md rounded-lg border bg-white p-6 shadow-xl">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Add Payment Method
                </h3>
                <button
                  onClick={handleCancel}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isProcessing}
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="mb-6">
                <PaymentElement
                  options={{
                    fields: {
                      billingDetails: {
                        name: 'auto',
                        email: 'auto',
                        phone: 'auto',
                        address: 'auto',
                      },
                    },
                    paymentMethodOrder: ['card'],
                  }}
                />
              </div>

              {message && (
                <div
                  className={`mb-4 rounded-md p-3 ${
                    message.includes('successfully')
                      ? 'border border-green-200 bg-green-100 text-green-800'
                      : 'border border-red-200 bg-red-100 text-red-800'
                  }`}
                >
                  {message}
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={handleAddPaymentMethod}
                  className={`flex-1 ${!stripe || isProcessing ? 'cursor-not-allowed opacity-50' : ''}`}
                >
                  {isProcessing ? (
                    <div className="flex items-center justify-center">
                      <svg
                        className="mr-3 -ml-1 h-5 w-5 animate-spin text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Adding...
                    </div>
                  ) : (
                    'Add'
                  )}
                </Button>
                <button
                  onClick={handleCancel}
                  disabled={isProcessing}
                  className="flex-1 rounded-md border border-gray-300 px-4 py-3 font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Bills;
